import {Button, Container, Loader, Stack, Text, Title} from '@mantine/core'
import {createFileRoute, Link, useNavigate} from '@tanstack/react-router'
import {useEffect, useRef, useState} from 'react'

import {UnauthorizedError, verify} from '../auth/auth-api'
import {AuthBrandHeader} from '../auth/auth-brand-header'
import {useAuthContext} from '../auth/auth-context'

type VerifySearch = {
    token: string
}

type PageState = 'checkingSession' | 'ready' | 'verifying' | 'error'

function verifyErrorMessage(err: unknown): string {
    if (err instanceof UnauthorizedError) {
        if (err.reason === 'expired') {
            return 'This link has expired. Request a new login link.'
        }
        if (err.reason === 'consumed') {
            return 'This link has already been used. Request a new login link.'
        }
        // Invalid, cut off or mangled links
        return 'This link is not valid. It may be incomplete. Request a new login link.'
    }
    return err instanceof Error ? err.message : 'Something went wrong'
}

export const Route = createFileRoute('/verify')({
    validateSearch: (search: Record<string, unknown>): VerifySearch => ({
        token: typeof search['token'] === 'string' ? search['token'] : '',
    }),
    component: VerifyPage,
})

function VerifyPage() {
    const {token} = Route.useSearch()
    const navigate = useNavigate()
    const {user, isLoading, signIn} = useAuthContext()
    const [pageState, setPageState] = useState<PageState>('checkingSession')
    const [error, setError] = useState<string | null>(null)
    const verifyingRef = useRef(false)

    useEffect(() => {
        if (!token) {
            setError('Invalid or missing login link.')
            setPageState('error')
            return
        }

        if (isLoading) {
            return
        }

        if (user) {
            navigate({to: '/'})
            return
        }

        setPageState('ready')
    }, [token, navigate, user, isLoading])

    const handleVerify = async () => {
        if (!token || verifyingRef.current) {
            return
        }

        verifyingRef.current = true
        setPageState('verifying')
        setError(null)

        try {
            const verifiedUser = await verify(token)
            signIn(verifiedUser)
            navigate({to: '/'})
        } catch (err) {
            setError(verifyErrorMessage(err))
            setPageState('error')
        } finally {
            verifyingRef.current = false
        }
    }

    if (pageState === 'error') {
        return (
            <Container size="sm" py="xl">
                <AuthBrandHeader />
                <Stack gap="md">
                    <Title order={1}>Unable to log in</Title>
                    <Text c="red">{error}</Text>
                    <Text>
                        <Link to="/login">Request a new login link</Link>
                    </Text>
                </Stack>
            </Container>
        )
    }

    if (pageState === 'checkingSession' || pageState === 'verifying') {
        return (
            <Container size="sm" py="xl">
                <AuthBrandHeader />
                <Stack gap="md" align="center">
                    <Loader />
                    <Text>
                        {pageState === 'checkingSession'
                            ? 'Checking your session...'
                            : 'Signing you in...'}
                    </Text>
                </Stack>
            </Container>
        )
    }

    return (
        <Container size="sm" py="xl">
            <AuthBrandHeader />
            <Stack gap="md">
                <Title order={1}>Sign in</Title>
                <Text>Click the button below to complete your login.</Text>
                <Button onClick={handleVerify}>Sign in</Button>
            </Stack>
        </Container>
    )
}
